import { useEffect, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Sparkles, Megaphone, ShoppingCart, ClipboardList, CheckCircle2, Loader2 } from "lucide-react";

const POSITION_ICON = {
  marketing: Megaphone,
  material_runner: ShoppingCart,
  secretarial: ClipboardList,
};

export default function Careers() {
  const [positions, setPositions] = useState([]);
  const [selected, setSelected] = useState("marketing");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", why_interested: "", resume_url: "", website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/careers/positions").then((r) => setPositions(r.data.positions || []));
  }, []);

  const submit = async () => {
    if (form.name.trim().length < 2) return toast.error("Enter your full name");
    if (!form.email.match(/^\S+@\S+\.\S+$/)) return toast.error("Enter a valid email");
    if (form.phone.trim().length < 7) return toast.error("Enter a valid phone number");
    if (form.why_interested.trim().length < 20) return toast.error("Tell us a bit more about why you're a fit (at least 20 characters)");
    setSubmitting(true);
    try {
      await api.post("/careers/applications", {
        ...form,
        position_id: selected,
      });
      setSubmitted(true);
      toast.success("Application received — we'll be in touch.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPos = positions.find((p) => p.id === selected);

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="max-w-3xl">
          <Badge className="rounded-full bg-terracotta/15 text-terracotta border-0" data-testid="careers-badge">
            <Sparkles className="w-3 h-3 mr-1" /> We&apos;re hiring
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-charcoal mt-4">Join the crew.</h1>
          <p className="text-charcoal/70 mt-3 text-lg leading-relaxed">
            Move Day Pro&apos;s exists because move-in week is chaos. If you can help us tame it — whether by drumming up demand, keeping the schedule tight, or ferrying mount brackets across town — we want to hear from you.
          </p>
        </div>

        {/* Position cards */}
        <div className="mt-10 grid gap-4 md:grid-cols-3" data-testid="position-cards">
          {positions.map((p) => {
            const Icon = POSITION_ICON[p.id] || Sparkles;
            const active = selected === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelected(p.id)}
                data-testid={`position-card-${p.id}`}
                className={`text-left p-5 rounded-2xl border transition-all ${
                  active
                    ? "border-terracotta bg-[#131C2E] shadow-lg scale-[1.01]"
                    : "border-[#1F2A40] bg-[#0F1A2E] hover:border-terracotta/50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-terracotta text-white" : "bg-terracotta/10 text-terracotta"}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-lg text-charcoal leading-tight">{p.title}</h3>
                    <p className="text-xs text-charcoal/50 mt-0.5">{p.type}</p>
                  </div>
                </div>
                <p className="text-sm text-charcoal/75 mt-3 leading-relaxed">{p.summary}</p>
              </button>
            );
          })}
        </div>

        {/* Details + form */}
        {selectedPos && (
          <div className="mt-10 grid gap-8 lg:grid-cols-5">
            {/* Role details */}
            <div className="lg:col-span-3 bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-6 lg:p-8">
              <h2 className="font-display text-3xl text-charcoal" data-testid="role-title">{selectedPos.title}</h2>
              <p className="text-charcoal/60 mt-1">{selectedPos.type}</p>
              <p className="text-charcoal/80 mt-4 leading-relaxed">{selectedPos.summary}</p>

              <div className="mt-6">
                <h3 className="font-display text-lg text-charcoal">What you&apos;ll do</h3>
                <ul className="mt-3 space-y-2">
                  {selectedPos.responsibilities.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm text-charcoal/80">
                      <CheckCircle2 className="w-4 h-4 text-sage mt-0.5 shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="font-display text-lg text-charcoal">Who we&apos;re looking for</h3>
                <ul className="mt-3 space-y-2">
                  {selectedPos.qualifications.map((q) => (
                    <li key={q} className="flex items-start gap-2 text-sm text-charcoal/80">
                      <CheckCircle2 className="w-4 h-4 text-terracotta mt-0.5 shrink-0" />
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Application form */}
            <div className="lg:col-span-2 bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-6 lg:p-8 h-fit lg:sticky lg:top-24" data-testid="application-form">
              {submitted ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-sage/20 text-sage flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h3 className="font-display text-xl text-charcoal mt-4">Application received</h3>
                  <p className="text-charcoal/70 text-sm mt-2 leading-relaxed">
                    Thanks — we&apos;ll review and reach out if there&apos;s a fit. Move-in week is around the corner, so we move fast.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-display text-2xl text-charcoal">Apply now</h3>
                  <p className="text-charcoal/60 text-sm mt-1">Applying for <span className="text-terracotta font-medium">{selectedPos.title}</span></p>
                  <div className="mt-5 space-y-3">
                    <div>
                      <Label htmlFor="name">Full name</Label>
                      <Input id="name" data-testid="app-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" data-testid="app-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-lg mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" data-testid="app-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-lg mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="resume">Resume link (LinkedIn / Google Drive / Dropbox)</Label>
                      <Input id="resume" data-testid="app-resume" placeholder="https://…" value={form.resume_url} onChange={(e) => setForm({ ...form, resume_url: e.target.value })} className="rounded-lg mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="why">Why you&apos;re a fit</Label>
                      <Textarea id="why" data-testid="app-why" rows={5}
                        placeholder="Give us the short version — what you'd bring, what you've built, and why now."
                        value={form.why_interested}
                        onChange={(e) => setForm({ ...form, why_interested: e.target.value })}
                        className="rounded-lg mt-1" />
                    </div>
                    {/* Honeypot: hidden from users, catches bots. */}
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                      style={{ position: "absolute", left: "-9999px", opacity: 0 }}
                      aria-hidden="true"
                    />
                    <Button
                      onClick={submit}
                      disabled={submitting}
                      data-testid="app-submit"
                      className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta/90 text-white btn-lift mt-2"
                    >
                      {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : "Submit application"}
                    </Button>
                    <p className="text-[11px] text-charcoal/50 text-center mt-1">
                      We reply within 5 business days.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
